import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ClipboardCheck, ClipboardList, Loader2, XCircle } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const box = { background: '#0F1923', border: '1px solid #2D3748' };
const TONE = {
  pass: { color: '#00C896', Icon: CheckCircle2, label: 'OK' },
  warn: { color: '#F59E0B', Icon: AlertTriangle, label: 'Check' },
  fail: { color: '#EF4444', Icon: XCircle, label: 'Fix' },
};

const CheckRow = ({ c, onExempt, busyId }) => {
  const t = TONE[c.status] || TONE.warn;
  return (
    <div className="flex gap-3 py-2.5 border-b last:border-b-0" style={{ borderColor: '#1F2A37' }} data-testid={`readiness-check-${c.id}`}>
      <t.Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.color }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white">{c.title}</p>
          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded" style={{ color: t.color, background: `${t.color}22` }} data-testid={`readiness-status-${c.id}`}>{t.label}</span>
        </div>
        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{c.detail}</p>
        {c.fix && c.status !== 'pass' && <p className="text-xs mt-1" style={{ color: '#CBD5E1' }}><span className="font-semibold">How to fix:</span> {c.fix} <a href={`/user-manual?tab=admin&fix=readiness_${c.id}#fix-readiness_${c.id}`} target="_blank" rel="noreferrer" className="underline" style={{ color: '#7DD3FC' }} data-testid={`readiness-fix-link-${c.id}`}>Step-by-step ↗</a></p>}
        {c.unmatched?.length > 0 && (
          <ul className="mt-1.5 space-y-1" data-testid="readiness-unmatched-list">
            {c.unmatched.map((u) => (
              <li key={u.worker_id} className="text-xs flex flex-wrap items-center gap-2" style={{ color: '#FCA5A5' }}>
                <span>• {u.name}{u.hint ? <span style={{ color: '#94A3B8' }}> — {u.hint}</span> : null}</span>
                {onExempt && (
                  <button type="button" onClick={() => onExempt(u)} disabled={busyId === u.worker_id}
                    className="px-2 py-0.5 rounded border border-sky-700/50 text-sky-300 hover:bg-sky-500/10 text-[11px] disabled:opacity-50"
                    title="Not paid through QuickBooks (test account, contractor, owner) — skip them in every push and readiness check"
                    data-testid={`readiness-exempt-${u.worker_id}`}>
                    {busyId === u.worker_id ? 'Saving…' : 'Mark exempt'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Settings → QuickBooks Online: one-click "Payroll Readiness Check" (Sep 2026).
export const PayrollReadinessCheck = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/qb/payroll-readiness`, { headers: { Authorization: `Bearer ${token}` } });
      setReport(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'The readiness check could not run — please try again.');
    } finally {
      setBusy(false);
    }
  };
  const [busyId, setBusyId] = useState('');
  const markExempt = async (u) => {
    setBusyId(u.worker_id);
    try {
      await axios.put(`${API}/users/${u.worker_id}/edit`, { qb_payroll_exempt: true }, { headers: { Authorization: `Bearer ${token}` } });
      await run();
    } catch (e) {
      setError(e.response?.data?.detail || `Could not mark ${u.name} exempt.`);
    } finally {
      setBusyId('');
    }
  };
  const headline = report ? (report.ready ? (report.warns ? `Ready to push — ${report.warns} item(s) worth a look` : 'Ready to push payroll to QuickBooks') : `${report.fails} blocker(s) must be fixed before pushing`) : null;
  const headColor = report ? (report.ready ? (report.warns ? '#F59E0B' : '#00C896') : '#EF4444') : '#94A3B8';
  return (
    <div className="mt-4 p-4 rounded-xl" style={box} data-testid="payroll-readiness-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">Payroll Readiness Check</h4>
          <p className="text-xs" style={{ color: '#94A3B8' }}>Runs every check at once — live environment, connection, tokens, API version, time tracking, employee matches, names, open shifts — and tells you in plain English what to fix.</p>
        </div>
        <button onClick={run} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          style={{ background: '#00C896', color: '#0F1923' }} data-testid="payroll-readiness-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} {busy ? 'Checking…' : 'Run readiness check'}
        </button>
      </div>
      {error && <p className="mt-3 text-xs font-semibold" style={{ color: '#EF4444' }} data-testid="payroll-readiness-error">{error}</p>}
      {report && (
        <div className="mt-3" data-testid="payroll-readiness-report">
          <p className="text-sm font-semibold" style={{ color: headColor }} data-testid="payroll-readiness-headline">{headline}</p>
          <p className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>
            {report.passes} passed · {report.warns} to check · {report.fails} to fix · checked {new Date(report.checked_at).toLocaleTimeString()} · method: {report.push_method}
          </p>
          <div className="mt-2">{report.checks.map((c) => <CheckRow key={c.id} c={c} onExempt={c.id === 'worker_match' ? markExempt : null} busyId={busyId} />)}</div>
          <div className="mt-4 p-3 rounded-lg" style={{ background: '#111C28', border: '1px dashed #2D3748' }} data-testid="payroll-readiness-manual">
            <p className="text-xs font-semibold text-white flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" style={{ color: '#60A5FA' }} /> Confirm inside QuickBooks (not visible to HinesTime)</p>
            <ul className="mt-2 space-y-2">
              {report.manual.map((m) => (
                <li key={m.id} className="text-xs" data-testid={`readiness-manual-${m.id}`}>
                  <span className="font-medium text-white">{m.title}</span>
                  <p style={{ color: '#94A3B8' }}>{m.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
